import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { SelectWithCreate } from '../../components/ui/select-with-create';
import { Checkbox } from '../../components/ui/checkbox';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Shield, Plus, ChevronLeft, ChevronRight, Eye, Edit, Trash2, Copy, 
  ChevronUp, ChevronDown, ChevronsUpDown,
  CheckCircle, AlertTriangle, Activity 
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const initialFormData = {
  nombre: '',
  ubicacion: '',
  modelo: '',
  ip_mgmt_lan: '',
  ip_mgmt_wan: '',
  port: '',
  nro_serie: '',
  firmware: '',
  last_firmware: '',
  generic_monitoring: 0,
  specific_monitoring: 0,
  sitio: '',
  status_id: '',
  comentarios: '',
};

export default function Firewalls() {
  const [firewalls, setFirewalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, monitored: 0 });
  const [sortConfig, setSortConfig] = useState({ field: 'nombre', dir: 'asc' });

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    nombre: '',
    ip_mgmt_lan: '',
    modelo: '',
    sitio: '',
    status_id: '',
    firmware: '',
  });

  // Form options from API (for create/edit modal)
  const [formOptions, setFormOptions] = useState({ modelos: [], ubicaciones: [], sites: [], estados: [] });
  
  // Filter options (only values that exist in firewalls table)
  const [filterOptions, setFilterOptions] = useState({ modelos: [], sites: [], estados: [] });

  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { canCreate, canUpdate, canDelete } = usePermissions('assets', 'firewalls');

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(firewalls);

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/firewalls/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/firewalls/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/firewalls/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFirewalls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });

      // Add column filters
      if (columnFilters.nombre) params.append('search', columnFilters.nombre);
      if (columnFilters.modelo && columnFilters.modelo !== 'all') params.append('modelo', columnFilters.modelo);
      if (columnFilters.sitio && columnFilters.sitio !== 'all') params.append('sitio', columnFilters.sitio);
      if (columnFilters.status_id && columnFilters.status_id !== 'all') params.append('status_id', columnFilters.status_id);

      const response = await api.get(`/assets/firewalls?${params}`);
      setFirewalls(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar firewalls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFormOptions(); fetchFilterOptions(); fetchStats(); }, []);
  useEffect(() => { fetchFirewalls(); }, [pagination.page, pagination.limit, sortConfig, columnFilters.status_id]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Debounced filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        fetchFirewalls();
      } else {
        setPagination(p => ({ ...p, page: 1 }));
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [columnFilters.nombre, columnFilters.modelo, columnFilters.sitio]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // Filter firewalls locally for non-API filters
  const filteredFirewalls = firewalls.filter(fw => {
    if (columnFilters.ip_mgmt_lan && !fw.ip_mgmt_lan?.toLowerCase().includes(columnFilters.ip_mgmt_lan.toLowerCase())) return false;
    if (columnFilters.firmware && !fw.firmware?.toLowerCase().includes(columnFilters.firmware.toLowerCase())) return false;
    return true;
  });

  const handleCreate = () => {
    setEditingItem(null);
    setCopyingFrom(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (firewall) => {
    setEditingItem(firewall);
    setCopyingFrom(null);
    setFormData({
      nombre: firewall.nombre || '',
      ubicacion: firewall.ubicacion?.toString() || '',
      modelo: firewall.modelo?.toString() || '',
      ip_mgmt_lan: firewall.ip_mgmt_lan || '',
      ip_mgmt_wan: firewall.ip_mgmt_wan || '',
      port: firewall.port || '',
      nro_serie: firewall.nro_serie || '',
      firmware: firewall.firmware || '',
      last_firmware: firewall.last_firmware || '',
      generic_monitoring: firewall.generic_monitoring || 0,
      specific_monitoring: firewall.specific_monitoring || 0,
      sitio: firewall.sitio?.toString() || '',
      status_id: firewall.status_id?.toString() || '',
      comentarios: firewall.comentarios || '',
    });
    setModalOpen(true);
  };

  const handleCopy = (firewall) => {
    setCopyingFrom(firewall);
    setEditingItem(null);
    setFormData({
      nombre: '', // Clear unique fields
      ubicacion: firewall.ubicacion?.toString() || '',
      modelo: firewall.modelo?.toString() || '',
      ip_mgmt_lan: '', // Clear unique
      ip_mgmt_wan: '', // Clear unique
      port: firewall.port || '',
      nro_serie: '', // Clear unique
      firmware: firewall.firmware || '',
      last_firmware: firewall.last_firmware || '',
      generic_monitoring: firewall.generic_monitoring || 0,
      specific_monitoring: firewall.specific_monitoring || 0,
      sitio: firewall.sitio?.toString() || '',
      status_id: firewall.status_id?.toString() || '',
      comentarios: '',
    });
    setCopyModalOpen(true);
  };

  const handleView = (firewall) => {
    setViewingItem(firewall);
    setDetailOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.ubicacion || !formData.ip_mgmt_lan) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        ubicacion: parseInt(formData.ubicacion) || null,
        modelo: formData.modelo ? parseInt(formData.modelo) : null,
        sitio: formData.sitio ? parseInt(formData.sitio) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
      };

      if (editingItem) {
        await api.put(`/assets/firewalls/${editingItem.id}`, payload);
        toast.success('Firewall actualizado exitosamente');
      } else {
        await api.post('/assets/firewalls', payload);
        toast.success(copyingFrom ? 'Firewall copiado exitosamente' : 'Firewall creado exitosamente');
      }
      setModalOpen(false);
      setCopyModalOpen(false);
      fetchFirewalls();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (firewall) => {
    try {
      await api.delete(`/assets/firewalls/${firewall.id}`);
      toast.success('Firewall eliminado exitosamente');
      setDeleteConfirm(null);
      fetchFirewalls();
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar firewall');
    }
  };

  const handleToggleMonitoring = async (firewall, field) => {
    if (!canUpdate) return;
    try {
      const payload = { [field]: !firewall[field] };
      await api.patch(`/assets/firewalls/${firewall.id}/monitoring`, payload);
      setFirewalls(prev =>
        prev.map(fw =>
          fw.id === firewall.id ? { ...fw, [field]: !fw[field] } : fw
        )
      );
      if (viewingItem?.id === firewall.id) {
        setViewingItem(prev => ({ ...prev, [field]: !prev[field] }));
      }
      toast.success('Monitoreo actualizado');
    } catch (error) {
      toast.error('Error al actualizar monitoreo');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Renderizar campos del formulario (inline para evitar re-mount)
  const renderFormFields = (showUniqueSection = false) => (
    <div className="space-y-6">
      {/* Unique Fields Section (for copy modal) */}
      {showUniqueSection && (
        <div className="p-4 border border-warning/30 rounded-lg bg-warning/5">
          <h4 className="text-sm font-semibold mb-3 text-warning flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Campos unicos (obligatorio completar)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                className="input-subtle mt-1.5"
                data-validation={!formData.nombre ? 'required-empty' : ''}
                placeholder="FGT-NUEVO-01"
                data-testid="input-nombre-copy"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IP Gestion LAN *</Label>
              <Input
                value={formData.ip_mgmt_lan}
                onChange={(e) => handleInputChange('ip_mgmt_lan', e.target.value)}
                className="input-subtle mt-1.5 font-mono"
                data-validation={!formData.ip_mgmt_lan ? 'required-empty' : ''}
                placeholder="10.0.0.X"
                data-testid="input-ip-lan-copy"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IP Gestion WAN</Label>
              <Input
                value={formData.ip_mgmt_wan}
                onChange={(e) => handleInputChange('ip_mgmt_wan', e.target.value)}
                className="input-subtle mt-1.5 font-mono"
                placeholder="200.10.10.X"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Nro Serie</Label>
              <Input
                value={formData.nro_serie}
                onChange={(e) => handleInputChange('nro_serie', e.target.value)}
                className="input-subtle mt-1.5 font-mono"
                placeholder="NUEVO-SERIAL"
              />
            </div>
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div className={showUniqueSection ? "p-4 border border-success/30 rounded-lg bg-success/5" : ""}>
        {showUniqueSection && (
          <h4 className="text-sm font-semibold mb-3 text-success flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Campos copiados (puede modificar)
          </h4>
        )}

        {!showUniqueSection && (
          <>
            <h4 className="text-sm font-semibold mb-3 text-primary">Informacion Basica</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className="text-sm font-medium">Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  className="input-subtle mt-1.5"
                  data-validation={!formData.nombre ? 'required-empty' : ''}
                  placeholder="FGT600E-1"
                  data-testid="input-nombre"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Ubicacion *</Label>
                <SelectWithCreate
                  value={formData.ubicacion}
                  onValueChange={(v) => handleInputChange('ubicacion', v)}
                  options={formOptions.ubicaciones || []}
                  placeholder="Seleccionar..."
                  createEndpoint="/assets/firewalls/quick-create/ubicacion"
                  entityName="Ubicación"
                  createFields={{
                    name: { label: "Ubicación", placeholder: "Ej: DATACENTER" },
                    description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                  }}
                  onCreated={(newItem) => {
                    fetchFormOptions();
                    handleInputChange('ubicacion', String(newItem.id));
                  }}
                  className="mt-1.5"
                />
              </div>
            </div>
          </>
        )}

        <h4 className="text-sm font-semibold mb-3 text-primary">Red</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {!showUniqueSection && (
            <>
              <div>
                <Label className="text-sm font-medium">IP Gestion LAN *</Label>
                <Input
                  value={formData.ip_mgmt_lan}
                  onChange={(e) => handleInputChange('ip_mgmt_lan', e.target.value)}
                  className="input-subtle mt-1.5 font-mono"
                  data-validation={!formData.ip_mgmt_lan ? 'required-empty' : ''}
                  placeholder="10.0.0.1"
                  data-testid="input-ip-lan"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">IP Gestion WAN</Label>
                <Input
                  value={formData.ip_mgmt_wan}
                  onChange={(e) => handleInputChange('ip_mgmt_wan', e.target.value)}
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="200.10.10.1"
                />
              </div>
            </>
          )}
          <div>
            <Label className="text-sm font-medium">Puerto</Label>
            <Input
              value={formData.port}
              onChange={(e) => handleInputChange('port', e.target.value)}
              className="input-subtle mt-1.5 font-mono"
              placeholder="443"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Sitio</Label>
            <SelectWithCreate
              value={formData.sitio}
              onValueChange={(v) => handleInputChange('sitio', v)}
              options={formOptions.sites || []}
              placeholder="Seleccionar..."
              createEndpoint="/assets/firewalls/quick-create/site"
              entityName="Site"
              createFields={{
                name: { label: "Nombre", placeholder: "Ej: TZ_NUEVO" },
                description: { label: "Descripción", placeholder: "Descripción (opcional)" }
              }}
              onCreated={(newItem) => {
                fetchFormOptions();
                handleInputChange('sitio', String(newItem.id));
              }}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Estado</Label>
            <Select
              value={formData.status_id || "none"}
              onValueChange={(v) => handleInputChange('status_id', v === "none" ? "" : v)}
            >
              <SelectTrigger 
                className="input-subtle mt-1.5"
                data-validation={formData.status_id && formData.status_id !== "none" ? 'valid' : ''}
              >
                <SelectValue placeholder="Seleccionar estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin estado</SelectItem>
                {formOptions.estados?.map(e => (
                  <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <h4 className="text-sm font-semibold mb-3 text-primary">Equipo</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Modelo</Label>
            <SelectWithCreate
              value={formData.modelo}
              onValueChange={(v) => handleInputChange('modelo', v)}
              options={formOptions.modelos || []}
              placeholder="Seleccionar..."
              createEndpoint="/assets/firewalls/quick-create/modelo"
              entityName="Modelo"
              createFields={{
                name: { label: "Modelo", placeholder: "Ej: FortiGate 600E" },
                description: { label: "Descripción", placeholder: "Descripción (opcional)" }
              }}
              onCreated={(newItem) => {
                fetchFormOptions();
                handleInputChange('modelo', String(newItem.id));
              }}
              className="mt-1.5"
            />
          </div>
          {!showUniqueSection && (
            <div>
              <Label className="text-sm font-medium">Nro Serie</Label>
              <Input
                value={formData.nro_serie}
                onChange={(e) => handleInputChange('nro_serie', e.target.value)}
                className="input-subtle mt-1.5 font-mono"
                placeholder="FG6H0ETB21907759"
              />
            </div>
          )}
          <div>
            <Label className="text-sm font-medium">Firmware Actual</Label>
            <Input
              value={formData.firmware}
              onChange={(e) => handleInputChange('firmware', e.target.value)}
              className="input-subtle mt-1.5 font-mono"
              placeholder="7.0.12"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Comentarios</Label>
            <Input
              value={formData.comentarios}
              onChange={(e) => handleInputChange('comentarios', e.target.value)}
              className="input-subtle mt-1.5"
              placeholder="Observaciones..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Firewalls</h1>
            <p className="page-subtitle">Gestion de firewalls de red</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/firewalls"
              filters={{ search: columnFilters.nombre }}
              filename="firewalls"
            />
            <Button
              className={`btn btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={canCreate ? handleCreate : undefined}
              disabled={!canCreate}
              title={!canCreate ? 'Sin permiso para crear' : undefined}
              data-testid="create-firewall-btn"
            >
              <Plus className="h-4 w-4" />
              Nuevo Firewall
            </Button>
          </div>
        </div>

        {/* Stats - Same pattern as Servers */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="firewalls-stats">
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Shield className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total firewalls</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-monitored">
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.monitored}</div>
              <div className="stats-card-label">Monitoreados</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.total - stats.monitored}</div>
              <div className="stats-card-label">Sin monitoreo</div>
            </div>
          </div>
        </div>

        {/* Table - Same pattern as Servers */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      {/* Headers Row */}
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('nombre')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="nombre" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_mgmt_lan')}
                        >
                          <div className="flex items-center gap-1">
                            IP LAN
                            <SortIcon field="ip_mgmt_lan" />
                          </div>
                        </th>
                        <th>IP WAN</th>
                        <th>MODELO</th>
                        <th className="hidden lg:table-cell">SITIO</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('status_name')}
                        >
                          <div className="flex items-center gap-1">
                            ESTADO
                            <SortIcon field="status_name" />
                          </div>
                        </th>
                        <th className="hidden lg:table-cell">FIRMWARE</th>
                        <th className="w-28 text-right">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="w-10 py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={columnFilters.nombre}
                            onChange={(e) => handleFilterChange('nombre', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-nombre"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.ip_mgmt_lan}
                            onChange={(e) => handleFilterChange('ip_mgmt_lan', e.target.value)}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select value={columnFilters.modelo || "all"} onValueChange={(v) => handleFilterChange('modelo', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.modelos?.map(m => (
                                <SelectItem key={m.id} value={String(m.id)}>{m.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          <Select value={columnFilters.sitio || "all"} onValueChange={(v) => handleFilterChange('sitio', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.sites?.map(s => (
                                <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={columnFilters.status_id || "all"} onValueChange={(v) => handleFilterChange('status_id', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.estados?.map(e => (
                                <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden lg:table-cell py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.firmware}
                            onChange={(e) => handleFilterChange('firmware', e.target.value)}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFirewalls.length === 0 ? (
                        <tr>
                          <td colSpan={10}>
                            <div className="empty-state">
                              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron firewalls</p>
                            </div>
                          </td>
                        </tr>
                      ) : filteredFirewalls.map((fw) => (
                        <tr key={fw.id} data-testid={`firewall-row-${fw.id}`} className={isSelected(fw.id) ? 'bg-primary/5' : ''}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(fw.id)}
                              onCheckedChange={() => toggleRow(fw.id)}
                              aria-label={`Seleccionar ${fw.nombre}`}
                            />
                          </td>
                          <td className="font-medium text-primary">
                            <button
                              onClick={() => handleView(fw)}
                              className="hover:underline text-left"
                              data-testid={`firewall-link-${fw.id}`}
                            >
                              {fw.nombre}
                            </button>
                          </td>
                          <td className="font-mono text-sm text-muted-foreground">{fw.ip_mgmt_lan || '-'}</td>
                          <td className="font-mono text-sm text-muted-foreground">{fw.ip_mgmt_wan || '-'}</td>
                          <td>
                            {fw.modelo_nombre && (
                              <span className="badge badge-default">{fw.modelo_nombre}</span>
                            )}
                          </td>
                          <td className="hidden lg:table-cell text-muted-foreground">{fw.sitio_nombre || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              fw.status_name?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : fw.status_name?.toLowerCase().includes('baja') || fw.status_name?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {fw.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="hidden lg:table-cell font-mono text-sm text-muted-foreground">{fw.firmware || '-'}</td>
                          <td className="w-28 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => handleView(fw)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-firewall-${fw.id}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(fw)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={canUpdate ? "Editar" : "Sin permiso para editar"}
                                disabled={!canUpdate}
                                data-testid={`edit-firewall-${fw.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(fw)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={canCreate ? "Copiar registro" : "Sin permiso para crear"}
                                disabled={!canCreate}
                                data-testid={`copy-firewall-${fw.id}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteConfirm(fw)}
                                className={`action-btn-delete ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={canDelete ? "Eliminar" : "Sin permiso para eliminar"}
                                disabled={!canDelete}
                                data-testid={`delete-firewall-${fw.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      {pagination.total > 0 ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}` : '0 resultados'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pagination.limit.toString()}
                        onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8" data-testid="prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8" data-testid="next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Firewall' : 'Nuevo Firewall'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              {renderFormFields(false)}
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar Firewall: {copyingFrom?.nombre}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              {renderFormFields(true)}
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setCopyModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Copiando...' : 'Crear Copia'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="modal-content max-w-2xl">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                {viewingItem?.nombre}
              </DialogTitle>
            </DialogHeader>
            {viewingItem && (
              <div className="modal-body py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <p className="text-sm">{viewingItem.modelo_nombre || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p className="text-sm">{viewingItem.ubicacion_nombre || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">IP LAN</Label>
                    <p className="text-sm font-mono">{viewingItem.ip_mgmt_lan}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">IP WAN</Label>
                    <p className="text-sm font-mono">{viewingItem.ip_mgmt_wan || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Puerto</Label>
                    <p className="text-sm font-mono">{viewingItem.port || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Nro Serie</Label>
                    <p className="text-sm font-mono">{viewingItem.nro_serie || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Sitio</Label>
                    <p className="text-sm">{viewingItem.sitio_nombre || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Firmware</Label>
                    <p className="text-sm font-mono">{viewingItem.firmware || '-'}</p>
                  </div>
                </div>

                {/* Monitoring Section */}
                <Card className="nerve-card bg-primary/5 border-primary/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="h-4 w-4 text-primary" />
                      Monitoreo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                        <div>
                          <Label className="text-sm font-medium">Monitoreo General</Label>
                          <p className="text-xs text-muted-foreground">Monitoreo basico de disponibilidad</p>
                        </div>
                        <Switch
                          checked={Boolean(viewingItem.generic_monitoring)}
                          onCheckedChange={() => handleToggleMonitoring(viewingItem, 'generic_monitoring')}
                          disabled={!canUpdate}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                        <div>
                          <Label className="text-sm font-medium">Monitoreo Especifico</Label>
                          <p className="text-xs text-muted-foreground">Monitoreo detallado de metricas</p>
                        </div>
                        <Switch
                          checked={Boolean(viewingItem.specific_monitoring)}
                          onCheckedChange={() => handleToggleMonitoring(viewingItem, 'specific_monitoring')}
                          disabled={!canUpdate}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {viewingItem.comentarios && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Comentarios</Label>
                    <p className="text-sm">{viewingItem.comentarios}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="modal-footer">
              {canUpdate && (
                <Button variant="outline" onClick={() => { setDetailOpen(false); handleEdit(viewingItem); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              )}
              <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent className="bg-slate-900 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Firewall</AlertDialogTitle>
              <AlertDialogDescription>
                Esta seguro de eliminar el firewall "{deleteConfirm?.nombre}"?
                Esta accion no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-red-600 hover:bg-red-700">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Edit Bar - Solo sitio, NO estado_interno */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchFirewalls(); fetchStats(); }}
          endpoint="/assets/firewalls"
          options={{
            sites: formOptions.sites,
            ubicaciones: formOptions.ubicaciones,
            statuses: formOptions.estados?.map(e => ({ id: e.id, nombre: e.nombre || e.name }))
          }}
          fields={['sitio_id', 'ubicacion_id', 'status_id']}
        />
      </div>
    </Layout>
  );
}
